// #294 redesign (plan C16) — which critical popups this tab has closed with ✕. A module-level Set, so it
// lives as long as the page: a reload shows the popup again inside its sound window (CP 294 Section 1.1),
// and it is per tab (never shared). The popup only; the alert itself stays open.
import { useSyncExternalStore } from 'react'
import { useAuthStore } from '@/store/authStore'

export const MAX_POPUPS = 3

const hidden = new Set<number>()
let version = 0
const subscribers = new Set<() => void>()

const emit = () => {
  version += 1
  subscribers.forEach((cb) => cb())
}

export function hidePopupId(id: number): void {
  if (hidden.has(id)) return
  hidden.add(id)
  emit()
}

export function isPopupHidden(id: number): boolean {
  return hidden.has(id)
}

/** Forget every closed popup (logout: the next user starts clean). */
export function clearHiddenPopups(): void {
  if (hidden.size === 0) return
  hidden.clear()
  emit()
}

// Logout: the next person who signs in on this tab starts clean (review 2026-10-12). App's ProtectedRoute swaps
// the layout for <Navigate> in the same render as the sign-out, so the critical alerts controller never renders
// signed out: watch the auth store itself, on the change from signed in to signed out.
useAuthStore.subscribe((state, prev) => {
  if (prev.isAuthenticated && !state.isAuthenticated) clearHiddenPopups()
})

/** A number that changes whenever the closed set changes (to key a memo on). */
export function useHiddenPopupsVersion(): number {
  return useSyncExternalStore(
    (cb) => { subscribers.add(cb); return () => { subscribers.delete(cb) } },
    () => version,
  )
}

export function resetHiddenPopupsForTests(): void {
  hidden.clear()
  subscribers.clear()
  version = 0
}
